/**
 * useSearchBodies — 검색 응답 한 판(items) → 카드에 그릴 조판 본문 지도 `{ [String(problem_id)]: html }`.
 *
 * 규칙은 시험지 조립 창의 지역 함수 toSearchBodyMap(PaperComposeModal.jsx:252)과 **한 글자도 다르지 않다** —
 * 강의 화면과 조립 창이 같은 문제를 다른 규칙으로 그리면 한쪽만 고쳐도 빌드는 통과하고 화면도 떠서 조용히 갈린다.
 *  ① 키는 문자열(응답은 problem_id 를 정수로 준다) · problem_id 가 없으면 id
 *  ② body_lexical 이 null 인 문항은 **키를 넣지 않는다** — 그 카드가 평문 preview 로 폴백한다
 *  ③ 변환기(lexical·katex)를 못 받으면 전부 평문으로 남는다 — 검색 자체를 실패로 뒤집지 않는다
 * 조립 창 쪽은 load() 안에 묶여 있어 여기서 가져다 쓸 수 없다(export 안 됨). 조립 창은 고치지 않는다(이번 범위 밖).
 *
 * ── 조립 창과 다른 점 둘 ─────────────────────────────────────────────────
 * 조립 창은 응답을 받은 그 자리에서 **한 번만** 변환한다. 이 훅은 부품에 붙어 있어 같은 items 를 여러 번 본다
 * (StrictMode 의 effect 두 번, 다른 줄에 갔다 돌아와 결과 칸이 다시 서는 경우).
 *  - 변환기는 인자 doc 을 **제자리에서 고친다**(lexicalHtml.js:23). 객체로 온 body_lexical 을 그대로 넘기면
 *    두 번째 변환에서 수식이 겹으로 변환되고, 응답 객체(작업 상태 훅이 쥔 결과)까지 오염된다 → 넘기기 전에 복사한다.
 *  - 한 번 변환한 판은 items 배열을 열쇠로 기억한다(WeakMap). 결과 칸이 다시 서도 본문이 평문 → 조판으로
 *    한 번 더 번쩍이지 않는다. 배열이 버려지면 기억도 함께 사라진다.
 *
 * 늦게 끝난 변환은 세대 번호로 버린다 — 쪽을 넘기는 사이 앞 판의 본문이 뒤 판을 덮지 않게.
 */
import { useEffect, useRef, useState } from 'react';
import { toBodyHtmlMap } from './lexicalHtml';

const EMPTY = Object.freeze({});
const converted = new WeakMap(); // items 배열 → 본문 지도(성공한 것만 — 실패를 기억하면 다시 설 때 영영 평문이다)

const isObj = (v) => v !== null && typeof v === 'object';

/** 변환기가 제자리에서 고치므로 객체는 복사해 넘긴다(문자열 JSON 은 parseDoc 이 새로 만든다) */
function freshDoc(v) {
  if (!isObj(v)) return v;
  try { return JSON.parse(JSON.stringify(v)); } catch (_) { return v; }
}

/** 조립 창 toSearchBodyMap 의 앞 절반 — 변환할 editorState 지도 */
export function searchBodyDocs(items) {
  const docs = {};
  (Array.isArray(items) ? items : []).forEach((it) => {
    const pid = it?.problem_id ?? it?.id;
    if (pid != null && it?.body_lexical != null) docs[String(pid)] = freshDoc(it.body_lexical);
  });
  return docs;
}

const hasBody = (items) => Array.isArray(items) && items.some((it) => (it?.problem_id ?? it?.id) != null && it?.body_lexical != null);

export default function useSearchBodies(items) {
  const [state, setState] = useState(() => ({
    src: items, bodies: (isObj(items) && converted.get(items)) || EMPTY, loading: false,
  }));
  const genRef = useRef(0);
  const aliveRef = useRef(true);

  // StrictMode 는 마운트 → 정리 → 마운트를 돈다. 마운트마다 되살리지 않으면 dev 에서 영원히 평문이다
  useEffect(() => {
    aliveRef.current = true;
    return () => { aliveRef.current = false; };
  }, []);

  useEffect(() => {
    genRef.current += 1;
    const g = genRef.current;
    const hit = isObj(items) ? converted.get(items) : null;
    if (hit) { setState({ src: items, bodies: hit, loading: false }); return; }
    const docs = searchBodyDocs(items);
    if (Object.keys(docs).length === 0) { setState({ src: items, bodies: EMPTY, loading: false }); return; }
    // 변환이 끝날 때까지 앞 판의 지도를 그대로 둔다 — 같은 문제(같은 id)는 같은 본문이라 평문으로 번쩍이지 않고,
    // 다른 문제는 제 id 가 지도에 없어 평문으로 선다. 끝나면 이 판의 지도로 통째로 바꾼다.
    setState((s) => ({ src: items, bodies: s.bodies, loading: true }));
    toBodyHtmlMap(docs).then(
      (bodies) => {
        if (!aliveRef.current || g !== genRef.current) return;
        const map = bodies || EMPTY;
        if (isObj(items)) converted.set(items, map);
        setState({ src: items, bodies: map, loading: false });
      },
      (e) => {
        console.error('검색 결과 본문 변환기 로드 실패:', e);
        if (!aliveRef.current || g !== genRef.current) return;
        setState({ src: items, bodies: EMPTY, loading: false });
      },
    );
  }, [items]);

  // items 가 막 바뀌어 effect 가 아직 안 돈 렌더 — 기억한 판이 있으면 그것을, 없으면 앞 판을 그대로 보인다
  if (state.src !== items) {
    const hit = isObj(items) ? converted.get(items) : null;
    return hit ? { bodies: hit, loading: false } : { bodies: state.bodies, loading: hasBody(items) };
  }
  return { bodies: state.bodies, loading: state.loading };
}
